import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SelectEmailComponent } from "@/Common/Components/Contacts/EmailSelectionControl";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { UpcomingAppointment } from "@/Common/Models/Appointments/UpcomingAppointment";
import { Patient } from "@/Common/Models/Patient/Patient";
import { ConferenceRoom } from "@/Common/Models/Telemed/ConferenceRoom";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ConferenceRoomInformation } from "./ConferenceRoomInformation";

interface TelemedInvitationProps {
    patientInfo: Patient;
    onCloseCallback: () => void;
    onRoomUrlClickCallback: (roomUrl: string) => void;
    showAsPopup?: boolean;
}

class TelemedInvitationState {
    public hasLoadedExistingInfo: boolean;
    public isSendingInvitation: boolean; // used when user clicks send

    public selectedEmailAddress: string;
    public isCustomAdress: boolean;
    public emailContactTypeLookupId: string;
    public errorMessage: string;
    public createdRoom: ConferenceRoom;
    public existingRoomInfo: ConferenceRoom;
    public upcomingAppointments: UpcomingAppointment[];
    public shouldAddEmailAddressToPatientContact: boolean;

    public invitationTimeslotOptions: ComboBoxDropDownItem[];
    public selectedInvitationTimeslot: ComboBoxDropDownItem;
    public immediateTimeSlotOption: ComboBoxDropDownItem;

    public static getDefault(): TelemedInvitationState {
        let s = new TelemedInvitationState();
        s.hasLoadedExistingInfo = false;
        s.selectedEmailAddress = "";
        s.isCustomAdress = false;
        s.emailContactTypeLookupId = "";
        s.isSendingInvitation = false;
        s.createdRoom = null;
        s.invitationTimeslotOptions = null;
        s.immediateTimeSlotOption = new ComboBoxDropDownItem("_OPT_NOW", cacheStore.GetString("ImmediatelyLabel"));
        s.selectedInvitationTimeslot = s.immediateTimeSlotOption;
        s.shouldAddEmailAddressToPatientContact = false;
        return s;
    }
}

class TelemedInvitationResponse {
    public Errors: ErrorMessage[];
    public Room: ConferenceRoom;
}

class TelemedInvitationInformation {
    public Errors: ErrorMessage[];
    public ExistingRooms: ConferenceRoom[];
    public UpcomingAppointments: UpcomingAppointment[];
}

class TelemedInvitationComponent extends BaseReactComponent<TelemedInvitationProps, TelemedInvitationState> {
    public constructor(props: TelemedInvitationProps) {
        super(props);
        this.state = TelemedInvitationState.getDefault();
    }

    public componentDidMount(): void {
        super.setOverlay("TelemedInvitationComponent", {
            transparent: true,
            onClicked: () => this.props.onCloseCallback()
        });

        document.addEventListener("keyup", e => this.closeOnEscape(e));

        this.getExistingRoomInformation();
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        document.removeEventListener("keyup", e => this.closeOnEscape(e));
    }

    public render(): JSX.Element {
        let showAsPopupCss = this.props.showAsPopup ? "portal-register-modal" : "";

        var roomInfoToShow = this.state.existingRoomInfo;
        if (!this.shouldDisplayExistingInfo())
            roomInfoToShow = this.state.createdRoom;

        return (
            <div id="telemedInvicationComponent">
                <div className={`${showAsPopupCss} account-invitation`}>
                    <h3 className="header thin-font">{cacheStore.GetString("TelemedLabel")}</h3>
                    <div>

                        {this.hasError() && (
                            <div style={{ padding: "10px" }}>
                                <span className="red"> {this.state.errorMessage}</span>
                            </div>
                        )}

                        {(this.state.isSendingInvitation || !this.state.hasLoadedExistingInfo) && (
                            <div style={{ padding: "10px" }}>
                                <InformationLoadingComponent
                                    smallVersion={true}
                                    customMessage={cacheStore.GetString("WaitForTelemedServiceLabel")}
                                />
                            </div>
                        )}

                        {this.isNewInviteNoExisting() && !this.hasError() && (
                            <div style={{ padding: "10px" }}>
                                {JSXHelper.BreakStringIntoParagraphs(cacheStore.GetString("TelemedNewInvitationLabel"))}
                            </div>
                        )}

                        {(this.state.hasLoadedExistingInfo && roomInfoToShow != null) && !this.state.isSendingInvitation && (
                            <div style={{ padding: "10px" }}>
                                <ConferenceRoomInformation room={roomInfoToShow} openConferenceWindow={room => this.openConferenceWindow(room)} />
                            </div>
                        )}
                        <hr />

                        {this.shouldDisplaySendEmail() && (
                            <div>
                                <div style={{ paddingLeft: "10px" }}>
                                    <div style={{ fontWeight: "bold", display: "inline-block" }}>
                                        {cacheStore.GetString("TelemedSessionDateLabel")}
                                    </div>
                                    <div style={{ paddingLeft: "10px", display: "inline-block" }}>
                                        {this.getInvitationTimeSlotSelector()}
                                    </div>
                                </div>

                                <SelectEmailComponent
                                    patientInfo={this.props.patientInfo}
                                    onCloseDelegate={() => this.onCloseClick()}
                                    onSelectionCompletedDelegate={(emailAddress, contactTypeLookupId) => this.onEmailAddressChange(emailAddress, contactTypeLookupId)}
                                    onShouldAddEmailToPatientContactChangeDelegate={value => this.onAddEmailAddressToPatientContactChange(value)}
                                    onEnterKeyDelegate={() => this.onSendInvitationClick()}
                                />
                            </div>
                        )}

                        {this.renderActionsBtn()}

                    </div>
                </div>
            </div>
        );
    }

    private renderActionsBtn(): JSX.Element {
        let btns = [];
        btns.push(
            <button
                key="btn-cancel"
                className="btn btn-default push-right-5"
                onClick={() => this.onCloseClick()}
            >

                <span>{cacheStore.GetString("CloseLabel")}</span>
            </button>
        );

        btns.push(
            <button
                key="btn-refuse"
                disabled={!this.isValid()}
                className="btn btn-primary push-right-5"
                onClick={() => this.onSendInvitationClick()}
            >

                <span> {this.isNewInviteNoExisting() ? cacheStore.GetString("SendLabel") : cacheStore.GetString("TelemedResendInvitationLabel")}</span>
            </button>
        );

        return (
            <div className="footer clearfix">
                <div className="pull-right">
                    {btns}
                </div>
            </div>
        );
    }

    private onCloseClick(): void {
        if (typeof this.props.onCloseCallback === "function") {
            this.props.onCloseCallback();
        }
    }

    private onEmailAddressChange(emailAddress: string, contactTypeLookupId: string): void {
        this.mutateState(s => {
            s.emailContactTypeLookupId = contactTypeLookupId;
            s.selectedEmailAddress = emailAddress;
        });
    }

    private onAddEmailAddressToPatientContactChange(value: boolean): void {
        this.mutateState(s => {
            s.shouldAddEmailAddressToPatientContact = value;
        });
    }

    private onTimeSlotSelected(value: string): void {
        let selectedOption = ArrayHelper.Find(this.state.invitationTimeslotOptions, (x: ComboBoxDropDownItem) => x.Code == value);
        this.mutateState(s => {
            s.selectedInvitationTimeslot = selectedOption;
        });
    }

    private isValid(): boolean {
        return (
            this.state.selectedEmailAddress != null
            && this.state.selectedEmailAddress.length > 0
            && this.state.isSendingInvitation == false
            && !this.hasError()
        );
    }

    private isNewInviteNoExisting(): boolean {
        return this.state.hasLoadedExistingInfo && this.state.existingRoomInfo == null && !this.state.isSendingInvitation && this.state.createdRoom == null;
    }

    private shouldDisplaySendEmail(): boolean {
        return this.state.hasLoadedExistingInfo && !this.state.isSendingInvitation && !this.hasError();
    }

    private shouldDisplayExistingInfo(): boolean {
        return this.state.createdRoom == null;
    }

    private getInvitationTimeSlotSelector(): JSX.Element {
        return (
            <Combobox
                id="selectTimeslotTelemed"
                style={{ width: "auto", maxWidth: "475px" }}
                onChange={val => this.onTimeSlotSelected(val)}
                items={this.state.invitationTimeslotOptions != null ? this.state.invitationTimeslotOptions.toImmutable() : [].toImmutable()}
                selectedCode={this.state.selectedInvitationTimeslot.Code}
            />
        );
    }

    public hasError(): boolean {
        return this.state.errorMessage != null && this.state.errorMessage.length > 0;
    }

    private closeOnEscape(e: KeyboardEvent): void {
        if (e.keyCode == 27) {
            this.onCloseClick();
        }
    }

    private getExistingRoomInformation(): void {
        const client = new Client();

        let requestData = {
            PatientContactId: this.props.patientInfo.IndividualResponse.Contact_id,
            Email: this.state.selectedEmailAddress
        };

        client.Post(
            apiSettings.buildApiPath(),
            ServerOperations.Server.Myle_Patient_Telemed_GetInvitationInformation,
            requestData,
            response => this.onGetExistingRoomInformation(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.hasLoadedExistingInfo = false;
        });
    }

    private onGetExistingRoomInformation(response: TelemedInvitationInformation): void {
        if (response.Errors != null && response.Errors.length > 0) {
            this.mutateState(s => {
                s.hasLoadedExistingInfo = true;
                s.existingRoomInfo = null;
                s.upcomingAppointments = null;
                s.errorMessage = response.Errors[0].Message;
            });

            return;
        }
        else {
            this.mutateState(s => {
                s.hasLoadedExistingInfo = true;
                if (response.ExistingRooms != null && response.ExistingRooms.length > 0)
                    s.existingRoomInfo = response.ExistingRooms[0];
                else
                    s.existingRoomInfo = null;

                if (response.UpcomingAppointments != null && response.UpcomingAppointments.length > 0)
                    s.upcomingAppointments = response.UpcomingAppointments;
                else
                    s.upcomingAppointments = null;

                let selectionOptions: ComboBoxDropDownItem[] = [];
                selectionOptions.push(s.immediateTimeSlotOption);
                if (s.upcomingAppointments != null && s.upcomingAppointments.length > 0) {
                    let upcomingAppointmentSelections = s.upcomingAppointments.map((x: UpcomingAppointment) => {
                        let dateString = moment(x.DateTime).format("YYYY-MM-DD HH:mm A");
                        let employeeNameString = Employee.getFullNameWithPrefixContactFacet(x.EmployeeContactFacetId);
                        let displayString = dateString + " " + cacheStore.GetAppointmentDescription(x.Subject, x.ServiceDefinitionId, x.ComboId) + " : " + employeeNameString;

                        return new ComboBoxDropDownItem(x.AppointmentId, displayString, null, null, x);
                    });

                    Array.prototype.push.apply(selectionOptions, upcomingAppointmentSelections);
                }

                s.invitationTimeslotOptions = selectionOptions;
            });
        }
    }

    private onSendInvitationClick(): void {
        const client = new Client();

        let invitationDate: DateTime;
        if (this.state.selectedInvitationTimeslot == this.state.immediateTimeSlotOption)
            invitationDate = new Date();
        else
            invitationDate = (this.state.selectedInvitationTimeslot.OriginalItem as UpcomingAppointment).DateTime;

        let requestData = {
            AppointmentId: this.state.selectedInvitationTimeslot?.Code !== this.state.immediateTimeSlotOption?.Code
                ? this.state.selectedInvitationTimeslot?.Code
                : null,
            PatientContactId: this.props.patientInfo.IndividualResponse.Contact_id,
            Email: this.state.selectedEmailAddress,
            ShouldAddEmailAddressToPatientContact: this.state.shouldAddEmailAddressToPatientContact,
            PatientFirstName: this.props.patientInfo.IndividualResponse.FirstName,
            PatientPreferredName: this.props.patientInfo.IndividualResponse.PreferredName,
            PatientLastName: this.props.patientInfo.IndividualResponse.LastName,
            StartTime: moment(invitationDate).format(), // server expects DateTimeOffesst, moment.format() outputs correct string e.g."2017-06-16T12:00:00-04:00"
            ActiveSiteId: sessionStore.getCurrentEmployee().Model.ActiveSiteId,
            EmailContactTypeLookupId: this.state.emailContactTypeLookupId,
            ResendInvite: !this.isNewInviteNoExisting()
        };

        client.Post(
            apiSettings.buildApiPath(),
            ServerOperations.Server.Myle_Patient_Telemed_SendInvitation,
            requestData,
            response => this.onSendInvitationSuccess(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.isSendingInvitation = true;
        });
    }

    private onSendInvitationSuccess(response: TelemedInvitationResponse): void {
        if (this.state.shouldAddEmailAddressToPatientContact) this.reloadPatient();

        if (response.Errors != null && response.Errors.length > 0) {
            this.mutateState(s => {
                s.isSendingInvitation = false;
                s.createdRoom = null;
                s.errorMessage = response.Errors[0].Message;
            });

            return;
        }
        else {
            this.mutateState(
                s => {
                    s.isSendingInvitation = false;
                    s.createdRoom = response.Room;
                },
                () => {
                    const isImmediate = this.state.selectedInvitationTimeslot.DisplayName === cacheStore.GetString("ImmediatelyLabel");
                    if (isImmediate)
                        this.openConferenceWindow(this.state.createdRoom);

                    this.props.onCloseCallback();
                }
            );
        }
    }

    private onError(error: string): void {
        if (this.state.shouldAddEmailAddressToPatientContact) this.reloadPatient();

        this.mutateState(s => {
            s.hasLoadedExistingInfo = true;
            s.isSendingInvitation = false;
            s.errorMessage = cacheStore.GetString("ErrorSendingMessageToPortalLabel");
            console.log(error);
        });
    }

    private reloadPatient(): void {
        patientStore.ActionsProfile.loadPatient(this.props.patientInfo.IndividualResponse.Contact_id);
    }

    private openConferenceWindow(room: ConferenceRoom, e?: React.MouseEvent): void {
        if (e && e.preventDefault)
            e.preventDefault();

        window.setTimeout(
            () => {
                const roomUrl = apiSettings.buildAppUrl(
                    "api/telemed/login",
                    {
                        auth: true,
                        params: {
                            redirectUrl: room.RoomUrl
                        }
                    }
                );

                if (settingsStore.configuration.get("IsTelemedWindowInMyleWindow") && cacheStore.ClinicSettings.EnableTelemedPictureInPicture) {
                    this.props.onRoomUrlClickCallback(roomUrl);
                }
                else {
                    const newWindow = window.open(roomUrl, "_medfarTelemedConference", "height = 480, width = 600,modal=yes,alwaysRaised=yes");
                    if (newWindow.focus) {
                        newWindow.focus();
                    }
                }
            },
            50
        );
    }
}

export { TelemedInvitationComponent };
